import type { CityConfig } from "../city-template/types";

export const kalamazooConfig: CityConfig = {
  city: "Kalamazoo",
  state: "Michigan",
  stateAbbr: "MI",
  slug: "kalamazoo",
  route: "/cities/kalamazoo",
  title: "Emergency Passport Kalamazoo MI | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Kalamazoo, Michigan — as fast as 24 hours. Fast passport renewal and new passport applications for Kalamazoo, Portage, Battle Creek, Comstock, and all Southwest Michigan residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Kalamazoo, MI",
  heroSubtitle: "Kalamazoo travelers — from Western Michigan University faculty to Pfizer and Stryker professionals to Southwest Michigan families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Kalamazoo County and Southwest Michigan.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20aerial%20view%20of%20Kalamazoo%20Michigan%20downtown%20with%20the%20Arcadia%20Creek%20Festival%20Place%2C%20modern%20buildings%20mixed%20with%20historic%20architecture%2C%20Western%20Michigan%20University%20campus%20visible%20in%20the%20background%2C%20lush%20green%20trees%20lining%20the%20streets%2C%20warm%20golden%20afternoon%20sunlight%2C%20vibrant%20Southwest%20Michigan%20city%20atmosphere%2C%20professional%20city%20photography%20without%20text%20or%20people&width=1800&height=700&seq=kalamazoo-hero-002&orientation=landscape",
  heroImageAlt: "Kalamazoo Michigan downtown skyline with Arcadia Creek and Western Michigan University campus",
  geoPosition: "42.2917;-85.5872",
  geoPlacename: "Kalamazoo, Michigan",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 W. Fort St, Suite 1300, Detroit, MI 48226",
    distance: "Approximately 2.5 hours east of Kalamazoo via I-94",
  },
  nearbyCitiesZips: [
    { name: "Kalamazoo", zips: "49001, 49003, 49004, 49005, 49006, 49007, 49008, 49009, 49019, 49048" },
    { name: "Portage", zips: "49002, 49024, 49081" },
    { name: "Battle Creek", zips: "49014, 49015, 49016, 49017, 49018, 49037" },
    { name: "Comstock", zips: "49041" },
    { name: "Oshtemo", zips: "49077" },
    { name: "Texas", zips: "49051" },
    { name: "Schoolcraft", zips: "49087" },
    { name: "Parchment", zips: "49004" },
    { name: "Galesburg", zips: "49053" },
    { name: "Augusta", zips: "49012" },
    { name: "Vicksburg", zips: "49097" },
    { name: "Climax", zips: "49034" },
    { name: "Marshall", zips: "49068, 49069" },
    { name: "Albion", zips: "49224" },
    { name: "Springfield", zips: "49037" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Detroit or Chicago — no 2.5-hour drive required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-building-line", title: "Corporate Travel Hub", desc: "Serving Pfizer, Stryker, Western Michigan University travel needs" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no road trip to Detroit or Chicago" },
  ],
  internalLinks: [
    { name: "Expedited passport in Portage, MI", route: "/states/michigan" },
    { name: "Emergency passport help in Battle Creek, MI", route: "/states/michigan" },
    { name: "Urgent passport services in Grand Rapids, MI", route: "/cities/grand-rapids" },
    { name: "Michigan passport services overview", route: "/states/michigan" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Kalamazoo, Michigan remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (2.5 hours east via I-94) or Chicago Passport Agency (2.5 hours southwest via I-94) — whichever is fastest. Kalamazoo residents can ship from any UPS or FedEx in Kalamazoo County.",
  fpcOfficeDistance: "Serving Kalamazoo, Portage, Battle Creek, Comstock, Oshtemo, Galesburg, Augusta, Vicksburg, Marshall, Albion, and all of Southwest Michigan",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Kalamazoo and Southwest Michigan. From downtown Kalamazoo near Western Michigan University to the Portage business corridor, from Battle Creek to the Marshall and Albion communities — we serve all of Kalamazoo County and beyond.",
  nearbyCitiesIntro: "We serve Kalamazoo and all nearby communities including Portage, Battle Creek, Comstock, Oshtemo, Texas, Schoolcraft, Parchment, Galesburg, Augusta, Vicksburg, Climax, Marshall, Albion, and Springfield. We also serve Van Buren County, Calhoun County, and western Michigan communities.",
  howItWorksIntro: "Most Kalamazoo travelers complete everything online and ship documents overnight. No need to drive 2.5 hours to Detroit or Chicago — our couriers handle both agencies and always pick the fastest route.",
  mistakesIntro: "Kalamazoo is a major Southwest Michigan hub with significant corporate travel from Pfizer and Stryker plus strong university travel demand. Here are the most common passport mistakes we see from Kalamazoo County travelers.",
  testimonialsIntro: "Kalamazoo travelers — from WMU faculty to corporate professionals to Southwest Michigan families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Kalamazoo, MI? Here are the most common ones we hear from Southwest Michigan travelers.",
  statePageRoute: "/states/michigan",
  statePageName: "Michigan",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 2.5 hours to Detroit or Chicago", "Full day commitment — difficult from Kalamazoo"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Detroit or Chicago agency — fastest option chosen", "No long drive required", "Perfect for corporate and university travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Detroit or Chicago agency — fastest option chosen", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Kalamazoo travelers with immediate departure needs — as fast as 24 hours. Our couriers use Detroit or Chicago agency — whichever is fastest. Perfect for last-minute corporate trips and university conference travel.",
      bullets: ["24–48 hour turnaround available", "Detroit or Chicago agency — fastest route chosen", "Southwest Michigan corporate and university travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Kalamazoo adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Southwest Michigan address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Kalamazoo residents and families — including WMU students, new citizens, and Southwest Michigan families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Kalamazoo residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Kalamazoo and Southwest Michigan families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Kalamazoo and Southwest Michigan residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Kalamazoo MI", description: "Urgent passport processing for Kalamazoo Michigan travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Kalamazoo MI", description: "Fast passport renewal for Kalamazoo Michigan residents with a previously-issued passport." },
    { name: "First-Time Passport Kalamazoo MI", description: "New passport application services for Kalamazoo Michigan residents applying for their first passport." },
    { name: "Child Passport Kalamazoo MI", description: "Minor passport services for Kalamazoo Michigan families with children under 16." },
  ],
  testimonials: [
    {
      name: "Dr. Patricia L.",
      location: "Kalamazoo, MI",
      rating: 5,
      text: "Conference in Barcelona in 5 days and my passport was expired. Fast Passport Center had my renewed passport in 2 days. I shipped from the UPS Store on Westnedge and the whole process was seamless. Outstanding.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Michael R.",
      location: "Portage, MI",
      rating: 5,
      text: "Family vacation to the Dominican Republic with 8 days notice. Four passports — two adult renewals and two first-time for our kids. Done in 5 days. We shipped from FedEx on Romence and everything was perfect.",
      service: "Family Passport Bundle",
    },
    {
      name: "Sarah K.",
      location: "Battle Creek, MI",
      rating: 5,
      text: "Business trip to Tokyo with 10 days notice. Fast Passport Center walked me through every step and had my passport in 3 days. The courier service is an absolute game-changer for Southwest Michigan.",
      service: "Emergency Passport",
    },
    {
      name: "James W.",
      location: "Kalamazoo, MI",
      rating: 5,
      text: "Lost my passport the week before a study abroad semester in Spain. Fast Passport Center replaced it in 48 hours. As a WMU student, I couldn't afford to miss my program. They saved my semester.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "kzoo-1",
      name: "Kalamazoo Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "411 N Burdick St",
      city: "Kalamazoo",
      state: "MI",
      zip: "49007",
      phone: "(269) 343-6341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Kalamazoo, near Western Michigan University",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown Kalamazoo acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to WMU campus and downtown office workers.",
      icon: "ri-mail-line",
    },
    {
      id: "kzoo-2",
      name: "Portage Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6151 S Westnedge Ave",
      city: "Portage",
      state: "MI",
      zip: "49002",
      phone: "(269) 329-3280",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Portage, approximately 5 miles south of downtown Kalamazoo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Portage, Oshtemo, and southern Kalamazoo residents. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "kzoo-3",
      name: "Battle Creek Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "80 McCamly St N",
      city: "Battle Creek",
      state: "MI",
      zip: "49017",
      phone: "(269) 963-3431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Battle Creek, approximately 25 miles east of Kalamazoo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Battle Creek, Springfield, and eastern Calhoun County. Good alternative for east-side travelers.",
      icon: "ri-mail-line",
    },
    {
      id: "kzoo-4",
      name: "Comstock Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5620 Gull Rd",
      city: "Comstock",
      state: "MI",
      zip: "49041",
      phone: "(269) 343-4454",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Comstock, approximately 4 miles northeast of downtown Kalamazoo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Comstock, Galesburg, and northeast Kalamazoo residents.",
      icon: "ri-mail-line",
    },
    {
      id: "kzoo-5",
      name: "Kalamazoo County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "201 W Kalamazoo Ave",
      city: "Kalamazoo",
      state: "MI",
      zip: "49007",
      phone: "(269) 384-8140",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Kalamazoo County Building",
      website: "https://www.kalcounty.com/",
      websiteLabel: "Kalamazoo County",
      notes: "County Clerk passport acceptance. Extended hours compared to post office locations. Call to confirm current passport acceptance hours.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2.5 hour drive to Detroit or Chicago", fpc: "No — ship from Kalamazoo" },
    { feature: "Agency Selection", agency: "Must choose one yourself", fpc: "We pick Detroit or Chicago — fastest available" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Kalamazoo corporate professionals and university travelers often discover passport issues just before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services near Westnedge Avenue or Stadium Drive sometimes produce photos that meet ID requirements but fail passport standards. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Kalamazoo County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking Acceptance Facilities Early Enough",
      description: "Kalamazoo Post Office appointments fill during summer and academic conference seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating the Drive to Detroit or Chicago",
      description: "Many Kalamazoo residents attempt to drive to a passport agency in an emergency. The 2.5–3 hour drive each way means a full day lost — our courier service is faster and more reliable.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming 4–6 Weeks Is Enough",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Winter Caribbean travel from AZO peaks January–March — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Kalamazoo area or Southwest Michigan." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near Westnedge Avenue or downtown Kalamazoo." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to Detroit or Chicago Passport Agency — whichever is fastest for you." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Kalamazoo address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Kalamazoo, MI?", a: "Fast Passport Center offers emergency passport processing in Kalamazoo, Michigan in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (2.5 hours east via I-94) or Chicago Passport Agency (2.5 hours southwest via I-94) — without you making the drive." },
    { q: "Does Kalamazoo have its own passport agency?", a: "No — Kalamazoo does not have a regional passport agency. The nearest facilities are the Detroit Passport Agency (approximately 2.5 hours east via I-94) and the Chicago Passport Agency (approximately 2.5 hours southwest via I-94). Fast Passport Center's courier service means Kalamazoo residents never need to make either drive." },
    { q: "Can I get an expedited passport in Portage, Battle Creek, or Marshall?", a: "Absolutely. Fast Passport Center serves all of Southwest Michigan including Portage, Battle Creek, Comstock, Oshtemo, Galesburg, Augusta, Vicksburg, Marshall, Albion, and all of Kalamazoo and Calhoun counties. Our service works the same way everywhere." },
    { q: "What airport do Kalamazoo travelers use for international flights?", a: "Kalamazoo/Battle Creek International Airport (AZO) is the primary airport for Kalamazoo and Southwest Michigan. AZO offers direct flights to Chicago, Detroit, Atlanta, and seasonal destinations. Most international travel connects through Chicago O'Hare (ORD), Detroit (DTW), or Atlanta (ATL). Gerald R. Ford International (GRR) in Grand Rapids is also an option 55 minutes north. Always verify passport validity for at least 6 months past your return date." },
    { q: "Can I use Chicago's passport agency from Kalamazoo?", a: "Yes. The Chicago Passport Agency is approximately 2.5 hours southwest of Kalamazoo via I-94. For some emergency cases, it may offer earlier appointments than Detroit. Fast Passport Center's couriers serve both agencies — we always choose the fastest option for your timeline." },
    { q: "How much does an expedited passport cost in Kalamazoo, MI?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Kalamazoo?", a: "Walgreens, CVS, and Meijer stores throughout Kalamazoo and Portage offer passport photos. UPS Store and FedEx Office locations also provide them. Meijer is a particularly convenient option in Southwest Michigan. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve Western Michigan University students?", a: "Yes. Fast Passport Center serves all of Southwest Michigan including Western Michigan University students, faculty, and staff. We understand academic conference schedules and study abroad deadlines. Our courier service is especially valuable for students who can't take a day off to drive to Detroit or Chicago." },
  ],
  photoLocations: [
    { name: "Meijer — Westnedge Kalamazoo", address: "5800 Gull Rd, Kalamazoo, MI 49048", hours: "Mon–Sun: 6AM–Midnight" },
    { name: "Walgreens — Stadium Drive Kalamazoo", address: "3500 Stadium Dr, Kalamazoo, MI 49008", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Portage", address: "7722 S Westnedge Ave, Portage, MI 49002", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Downtown Kalamazoo", address: "251 E Michigan Ave, Kalamazoo, MI 49007", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Michigan SOS — Kalamazoo", address: "3825 E Cork St, Kalamazoo, MI 49001", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Michigan Secretary of State for driver's licenses, REAL ID, and vehicle registration. Online reservations strongly recommended.", featured: true },
    { name: "Michigan SOS — Portage", address: "522 W Centre Ave, Portage, MI 49024", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Serves Portage, southern Kalamazoo, and Schoolcraft area. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "AZO",
      name: "Kalamazoo/Battle Creek International Airport",
      distance: "4 miles southeast of downtown Kalamazoo via I-94",
      address: "5235 Portage Rd, Portage, MI 49002",
      note: "Primary airport for Kalamazoo and Southwest Michigan. Direct flights to Chicago, Detroit, Atlanta, and seasonal destinations. Most international travel connects through Chicago O'Hare (ORD), Detroit (DTW), or Atlanta (ATL).",
      flightUrl: "https://www.azooairport.com/",
      flightLabel: "AZO Flight Info",
      featured: true,
    },
    {
      code: "GRR",
      name: "Gerald R. Ford International Airport",
      distance: "Approximately 55 minutes north of Kalamazoo via US-131",
      address: "5500 44th St SE, Grand Rapids, MI 49512",
      note: "Alternative airport for Kalamazoo travelers. Larger hub with more domestic and international connections. Direct flights to Atlanta, Dallas, Orlando, and seasonal Caribbean destinations.",
      flightUrl: "https://www.grr.org/",
      flightLabel: "GRR Flight Info",
      featured: false,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 2.5 hours southwest of Kalamazoo via I-94",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major hub for Southwest Michigan travelers seeking more international routes. United and American Airlines hub with global connections.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: false,
    },
  ],
};